import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ArrowRight, Sun, Moon } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { NAV_LINKS, PAGE_ACCENTS, PAGE_ACCENTS_LIGHT, tint } from "@/data/site";
import { useTheme } from "@/context/ThemeProvider";

const ThemeToggle = ({ theme, toggle, className = "" }) => (
  <button
    type="button"
    onClick={toggle}
    data-testid="theme-toggle-button"
    aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
    className={`grid h-10 w-10 place-items-center rounded-[10px] transition-colors ${className}`}
    style={{ background: "var(--surface-0)", border: "1px solid var(--stroke-0)", color: "var(--text-1)" }}
  >
    {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
  </button>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { theme, toggle } = useTheme();
  const accents = theme === "light" ? PAGE_ACCENTS_LIGHT : PAGE_ACCENTS;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className="sticky top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300"
      style={{
        background: scrolled ? "var(--nav-bg-scrolled)" : "var(--nav-bg-top)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderBottom: `1px solid ${scrolled ? "var(--stroke-0)" : "transparent"}`,
        boxShadow: scrolled ? "var(--card-shadow)" : "none",
      }}
    >
      <div className="mx-auto flex h-[64px] w-full max-w-6xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2" aria-label="Emailink home">
          <span
            className="font-display text-[22px] leading-none tracking-[0.14em]"
            style={{ color: "var(--page-accent)", textShadow: `0 0 26px ${tint(45)}` }}
          >
            EMAILINK
          </span>
          <span className="font-tech hidden text-[9px] uppercase tracking-[0.2em] sm:inline" style={{ color: "var(--text-3)" }}>
            studio
          </span>
        </Link>

        {/* Desktop links */}
        <nav className="hidden items-center gap-1 md:flex">
          {[...NAV_LINKS, { label: "Contact", to: "/contact" }].map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className="relative px-3 py-2">
              {({ isActive }) => (
                <span className="relative">
                  <span
                    className="text-[13px] tracking-[0.02em] transition-colors duration-200"
                    style={{ color: isActive ? "var(--text-0)" : "var(--text-2)" }}
                  >
                    {l.label}
                  </span>
                  {isActive && (
                    <span
                      className="absolute -bottom-1.5 left-0 h-[2px] w-full"
                      style={{ background: `linear-gradient(90deg, var(--page-accent), transparent)` }}
                    />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <ThemeToggle theme={theme} toggle={toggle} className="hidden sm:grid" />
          <Link
            to="/contact"
            data-testid="nav-primary-cta-button"
            className="hidden items-center gap-2 rounded-[10px] px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] transition-[background-color,box-shadow] duration-200 sm:inline-flex"
            style={{ background: tint(12), color: "var(--page-accent)", border: `1px solid ${tint(42)}` }}
          >
            Work with us
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-menu-button"
                aria-label="Open menu"
                className="grid h-10 w-10 place-items-center rounded-[10px] md:hidden"
                style={{ background: "var(--surface-0)", border: "1px solid var(--stroke-0)", color: "var(--text-0)" }}
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[80%] max-w-sm border-l" style={{ background: "var(--surface-solid)", borderColor: "var(--stroke-0)" }}>
              <div className="mb-8 mt-2 flex items-center justify-between">
                <span className="font-display text-[22px] tracking-[0.14em]" style={{ color: "var(--page-accent)" }}>EMAILINK</span>
                <ThemeToggle theme={theme} toggle={toggle} />
              </div>
              <nav className="flex flex-col gap-1">
                {[...NAV_LINKS, { label: "Contact", to: "/contact" }].map((l, i) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink to={l.to} end={l.to === "/"} className="flex items-center justify-between border-b py-4" style={{ borderColor: "var(--stroke-0)" }}>
                      {({ isActive }) => (
                        <>
                          <span className="text-[18px]" style={{ color: isActive ? "var(--text-0)" : "var(--text-1)" }}>{l.label}</span>
                          <span className="font-tech text-[11px]" style={{ color: accents[l.to] }}>0{i + 1}</span>
                        </>
                      )}
                    </NavLink>
                  </SheetClose>
                ))}
              </nav>
              <SheetClose asChild>
                <Link
                  to="/contact"
                  className="mt-8 flex items-center justify-center gap-2 rounded-[12px] py-4 text-[13px] font-semibold uppercase tracking-[0.08em]"
                  style={{ background: "var(--page-accent)", color: "var(--on-accent)" }}
                >
                  Book a call <ArrowRight className="h-4 w-4" />
                </Link>
              </SheetClose>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
